import { DraftRestorePrompt } from '@/components/DraftRestorePrompt';
import { useDraft } from '@/lib/useDraft';
import {
  ArrowLeft,
  Check,
  Inbox,
  Lock,
  RefreshCcw,
  Search,
  Send,
  Settings,
  Sparkles,
} from 'lucide-react';
/**
 * Unified communications inbox (migration 0076).
 *
 * Two columns:
 *   Left  — customer list with unread counts, filter chips, search.
 *   Right — selected customer's threaded messages across all bookings,
 *           composer + inline AI reply suggestions, internal-note toggle.
 *
 * Keyboard shortcuts:
 *   j / k        navigate customer list
 *   Enter        open selected thread (focus composer)
 *   r            open reply composer
 *   Shift+R      open AI-suggested reply (fetch a draft + prefill composer)
 *   m            mark thread read
 *   /            focus search
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type CommInboxFilter,
  type CommInboxSettings,
  type CommInboxSnapshot,
  type CommInboxThread,
  type CommInboxThreadResponse,
  draftReplyForBooking,
  getCommInboxSettings,
  getCommInboxSnapshot,
  getCommInboxThread,
  markCommInboxAllRead,
  markCommInboxThreadRead,
  postCommInboxInternalNote,
  sendBookingMessage,
  updateCommInboxSettings,
} from '../../lib/api';

const FILTERS: Array<{ key: CommInboxFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'awaiting', label: 'Awaiting reply' },
];

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function InboxPage() {
  const [filter, setFilter] = useState<CommInboxFilter>('all');
  const [search, setSearch] = useState('');
  const [snapshot, setSnapshot] = useState<CommInboxSnapshot | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [thread, setThread] = useState<CommInboxThreadResponse | null>(null);
  // Composer autosaves per customer thread so a frozen tab never eats a
  // half-written reply. Persistence is off until a thread is selected.
  const composerDraft = useDraft<string>(
    selectedCustomerId ? `inbox:composer:${selectedCustomerId}` : null,
    '',
  );
  const composer = composerDraft.value;
  const setComposer = composerDraft.setValue;
  const [composerInternal, setComposerInternal] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<CommInboxSettings | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [snapshotError, setSnapshotError] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const refreshSnapshot = useCallback(() => {
    getCommInboxSnapshot(filter, search)
      .then((s) => {
        setSnapshot(s);
        setSnapshotError(false);
      })
      .catch(() => {
        setSnapshot(null);
        setSnapshotError(true);
      });
  }, [filter, search]);

  useEffect(() => {
    refreshSnapshot();
  }, [refreshSnapshot]);

  useEffect(() => {
    getCommInboxSettings()
      .then(setSettings)
      .catch(() => setSettings(null));
  }, []);

  useEffect(() => {
    if (!selectedCustomerId) {
      setThread(null);
      setError(null);
      return;
    }
    setThread(null);
    setError(null);
    getCommInboxThread(selectedCustomerId)
      .then((t) => {
        setThread(t);
        const firstBooking = t.bookings[0];
        if (firstBooking) {
          setActiveBookingId(firstBooking.id);
        }
        // Auto-suggest: if any unread customer message AND tenant has auto-suggest on.
        const hasUnread = t.messages.some((m) => m.sender === 'customer' && !m.staffSeenAt);
        if (hasUnread && settings?.commInboxAutoSuggest) {
          const bid = firstBooking?.id;
          if (bid) {
            setAiBusy(true);
            draftReplyForBooking(bid)
              .then((r) => setComposer(r.draft))
              .catch(() => undefined)
              .finally(() => setAiBusy(false));
          }
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'failed to load conversation');
      });
  }, [selectedCustomerId, settings?.commInboxAutoSuggest]);

  const threads = snapshot?.threads ?? [];

  // Keyboard navigation.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? '';
      const inEditable = tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable;
      if (e.key === '/' && !inEditable) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (inEditable) return;
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        if (threads.length === 0) return;
        const idx = threads.findIndex((t) => t.customerId === selectedCustomerId);
        const next = e.key === 'j' ? Math.min(threads.length - 1, idx + 1) : Math.max(0, idx - 1);
        const target = threads[next === -1 ? 0 : next];
        if (target) setSelectedCustomerId(target.customerId);
        return;
      }
      if (e.key === 'Enter' && selectedCustomerId) {
        e.preventDefault();
        composerRef.current?.focus();
        return;
      }
      if (e.key === 'r' && !e.shiftKey && selectedCustomerId) {
        e.preventDefault();
        setComposerInternal(false);
        composerRef.current?.focus();
        return;
      }
      if (e.key === 'R' && e.shiftKey && selectedCustomerId) {
        e.preventDefault();
        void runAiDraft();
        return;
      }
      if (e.key === 'm' && selectedCustomerId) {
        e.preventDefault();
        void onMarkRead();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, selectedCustomerId, activeBookingId]);

  async function onMarkRead() {
    if (!selectedCustomerId) return;
    try {
      await markCommInboxThreadRead(selectedCustomerId);
      refreshSnapshot();
      if (selectedCustomerId) {
        const t = await getCommInboxThread(selectedCustomerId);
        setThread(t);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to mark as read');
    }
  }

  async function onMarkAllRead() {
    try {
      await markCommInboxAllRead();
      refreshSnapshot();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to mark all as read');
    }
  }

  async function runAiDraft(instructions?: string) {
    if (!activeBookingId) return;
    setAiBusy(true);
    setError(null);
    try {
      const r = await draftReplyForBooking(activeBookingId, instructions);
      setComposer(r.draft);
      composerRef.current?.focus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI draft failed');
    } finally {
      setAiBusy(false);
    }
  }

  async function onSend() {
    if (!selectedCustomerId || !activeBookingId || composer.trim().length === 0) return;
    setSending(true);
    setError(null);
    try {
      if (composerInternal) {
        await postCommInboxInternalNote(selectedCustomerId, activeBookingId, composer);
      } else {
        await sendBookingMessage(activeBookingId, composer);
      }
      setComposer('');
      composerDraft.clearDraft();
      const t = await getCommInboxThread(selectedCustomerId);
      setThread(t);
      refreshSnapshot();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'send failed');
    } finally {
      setSending(false);
    }
  }

  return (
    // On <md, show EITHER the customer list OR the open thread (keyed on
    // selectedCustomerId) with a back button — not stacked fixed-height panes.
    <div className="grid h-[calc(100dvh-3rem)] grid-cols-1 md:grid-cols-[360px_1fr]">
      <aside
        className={`${
          selectedCustomerId ? 'hidden md:flex' : 'flex'
        } flex-col border-r border-border bg-sidebar`}
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2 text-base font-semibold">
            <Inbox className="size-4" /> Inbox
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Inbox settings"
            >
              <Settings className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => refreshSnapshot()}
              className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Refresh"
            >
              <RefreshCcw className="size-4" />
            </button>
          </div>
        </header>

        <div className="border-b border-border p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers, email, message... (/)"
              className="w-full rounded border border-border bg-background py-1.5 pl-7 pr-2 text-sm"
            />
          </div>
          <div className="mt-2 flex gap-1.5">
            {FILTERS.map((f) => {
              const count = snapshot?.counts?.[f.key] ?? 0;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={
                    filter === f.key
                      ? 'rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground'
                      : 'rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-secondary'
                  }
                >
                  {f.label} <span className="num">({count})</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={onMarkAllRead}
            className="mt-2 w-full rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
          >
            <Check className="mr-1 inline size-3" />
            Mark all read
          </button>
        </div>

        {showSettings && settings && (
          <InboxSettingsPanel
            settings={settings}
            onSave={async (patch) => {
              await updateCommInboxSettings(patch);
              const fresh = await getCommInboxSettings();
              setSettings(fresh);
            }}
          />
        )}

        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 &&
            (snapshotError ? (
              <div className="space-y-2 px-4 py-8 text-center text-sm">
                <p className="text-destructive">Could not load conversations.</p>
                <button
                  type="button"
                  onClick={() => refreshSnapshot()}
                  className="inline-flex items-center gap-1.5 rounded border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-secondary"
                >
                  <RefreshCcw className="size-3" />
                  Retry
                </button>
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                {snapshot ? 'No conversations yet.' : 'Loading…'}
              </div>
            ))}
          {threads.map((t) => (
            <CustomerRow
              key={t.id}
              thread={t}
              selected={selectedCustomerId === t.customerId}
              onClick={() => setSelectedCustomerId(t.customerId)}
            />
          ))}
        </div>
      </aside>

      <section className={`${selectedCustomerId ? 'flex' : 'hidden md:flex'} flex-col`}>
        {selectedCustomerId === null ? (
          <div className="grid flex-1 place-items-center text-sm text-muted-foreground">
            Select a conversation to read or reply.
          </div>
        ) : (
          <ThreadPane
            onBack={() => setSelectedCustomerId(null)}
            thread={thread}
            composer={composer}
            composerInternal={composerInternal}
            composerRef={composerRef}
            activeBookingId={activeBookingId}
            error={error}
            aiBusy={aiBusy}
            sending={sending}
            onChangeComposer={setComposer}
            onChangeInternal={setComposerInternal}
            onSelectBooking={setActiveBookingId}
            onMarkRead={onMarkRead}
            onDraftAi={() => void runAiDraft()}
            onDraftAiInstr={(instr) => void runAiDraft(instr)}
            onSend={onSend}
            pendingDraft={composerDraft.pendingDraft}
            onRestoreDraft={composerDraft.restoreDraft}
            onDiscardDraft={composerDraft.discardDraft}
          />
        )}
      </section>
    </div>
  );
}

function CustomerRow({
  thread,
  selected,
  onClick,
}: {
  thread: CommInboxThread;
  selected: boolean;
  onClick: () => void;
}) {
  const name = thread.customer
    ? `${thread.customer.firstName} ${thread.customer.lastName}`.trim()
    : 'Unknown';
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        selected
          ? 'flex w-full flex-col items-start gap-1 border-b border-border bg-secondary px-3 py-2.5 text-left'
          : 'flex w-full flex-col items-start gap-1 border-b border-border px-3 py-2.5 text-left hover:bg-secondary'
      }
    >
      <div className="flex w-full items-baseline justify-between">
        <span className="text-sm font-medium text-foreground">{name}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {fmtRelative(thread.lastMessageAt)}
        </span>
      </div>
      <div className="flex w-full items-center gap-1.5">
        <span className="line-clamp-1 flex-1 text-xs text-muted-foreground">
          {thread.lastMessageSender === 'staff' ? 'You: ' : ''}
          {thread.lastMessageExcerpt || '(no content)'}
        </span>
        {thread.unreadCount > 0 && (
          <span className="num rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
            {thread.unreadCount}
          </span>
        )}
        {thread.awaitingReplySince && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
            Awaiting
          </span>
        )}
      </div>
    </button>
  );
}

function ThreadPane(props: {
  thread: CommInboxThreadResponse | null;
  composer: string;
  composerInternal: boolean;
  composerRef: React.RefObject<HTMLTextAreaElement | null>;
  activeBookingId: string | null;
  error: string | null;
  aiBusy: boolean;
  sending: boolean;
  onBack: () => void;
  onChangeComposer: (v: string) => void;
  onChangeInternal: (v: boolean) => void;
  onSelectBooking: (id: string) => void;
  onMarkRead: () => Promise<void>;
  onDraftAi: () => void;
  onDraftAiInstr: (instr: string) => void;
  onSend: () => Promise<void>;
  pendingDraft: string | null;
  onRestoreDraft: () => void;
  onDiscardDraft: () => void;
}) {
  const {
    thread,
    composer,
    composerInternal,
    composerRef,
    activeBookingId,
    error,
    aiBusy,
    sending,
    pendingDraft,
  } = props;
  const [aiInstr, setAiInstr] = useState('');
  if (!thread) {
    return (
      <div className="grid flex-1 place-items-center text-sm text-muted-foreground">
        {error ? (
          <div className="space-y-2 text-center">
            <p className="text-red-700">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 rounded border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-secondary"
            >
              <RefreshCcw className="size-3" />
              Retry
            </button>
          </div>
        ) : (
          'Loading…'
        )}
      </div>
    );
  }
  const name = `${thread.customer.firstName} ${thread.customer.lastName}`.trim();
  return (
    <>
      <header className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={props.onBack}
            aria-label="Back to conversations"
            className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground md:hidden"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div>
            <h2 className="text-base font-semibold text-foreground">{name}</h2>
            <p className="text-xs text-muted-foreground">
              {thread.customer.email ?? '—'} · {thread.bookings.length} booking
              {thread.bookings.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {thread.bookings.length > 1 && (
            <select
              value={activeBookingId ?? ''}
              onChange={(e) => props.onSelectBooking(e.target.value)}
              className="rounded border border-border bg-background px-2 py-1 text-xs"
            >
              {thread.bookings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.type} · {b.status}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => void props.onMarkRead()}
            className="rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
          >
            <Check className="mr-1 inline size-3" /> Mark read (m)
          </button>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto bg-background px-5 py-4">
        {thread.messages
          .slice()
          .reverse()
          .map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
      </div>

      <footer className="border-t border-border bg-card px-5 py-3">
        {error && <p className="mb-2 text-xs text-red-700">{error}</p>}
        <div className="mb-2 flex items-center gap-2">
          <button
            type="button"
            onClick={props.onDraftAi}
            disabled={aiBusy || !activeBookingId}
            className="inline-flex items-center gap-1.5 rounded border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-secondary disabled:opacity-50"
          >
            <Sparkles className="size-3" />
            {aiBusy ? 'Drafting…' : 'Suggest reply (⇧R)'}
          </button>
          <input
            value={aiInstr}
            onChange={(e) => setAiInstr(e.target.value)}
            placeholder="Optional AI instructions: 'short + apologise'"
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs"
          />
          <button
            type="button"
            disabled={aiBusy || aiInstr.trim().length === 0}
            onClick={() => props.onDraftAiInstr(aiInstr.trim())}
            className="rounded border border-border px-2 py-1 text-xs disabled:opacity-50"
          >
            Apply
          </button>
        </div>
        {pendingDraft !== null && (
          <div className="mb-2">
            <DraftRestorePrompt onRestore={props.onRestoreDraft} onDiscard={props.onDiscardDraft} />
          </div>
        )}
        <textarea
          ref={composerRef}
          value={composer}
          onChange={(e) => props.onChangeComposer(e.target.value)}
          placeholder={composerInternal ? 'Internal note (never sent)…' : 'Reply to customer… (r)'}
          rows={4}
          className={
            composerInternal
              ? 'w-full resize-none rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-sm'
              : 'w-full resize-none rounded border border-border bg-background px-2 py-1.5 text-sm'
          }
        />
        <div className="mt-2 flex items-center justify-between">
          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={composerInternal}
              onChange={(e) => props.onChangeInternal(e.target.checked)}
            />
            <Lock className="size-3" /> Internal note (never delivered)
          </label>
          <button
            type="button"
            onClick={() => void props.onSend()}
            disabled={sending || composer.trim().length === 0 || !activeBookingId}
            className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            <Send className="size-3" />
            {sending ? 'Sending…' : composerInternal ? 'Save note' : 'Send'}
          </button>
        </div>
      </footer>
    </>
  );
}

function MessageBubble({ message }: { message: CommInboxThreadResponse['messages'][number] }) {
  const fromCustomer = message.sender === 'customer';
  const isInternal = message.isInternalNote;
  return (
    <div className={fromCustomer ? 'flex justify-start' : 'flex justify-end'}>
      <div
        className={
          isInternal
            ? 'max-w-[75%] rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'
            : fromCustomer
              ? 'max-w-[75%] rounded-2xl bg-secondary px-3 py-2 text-sm'
              : 'max-w-[75%] rounded-2xl bg-primary px-3 py-2 text-sm text-primary-foreground'
        }
      >
        {isInternal && (
          <div className="mb-1 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide">
            <Lock className="size-2.5" /> Internal
          </div>
        )}
        <p className="whitespace-pre-wrap">{message.body || <em>(deleted)</em>}</p>
        <p
          className={
            fromCustomer || isInternal
              ? 'mt-1 text-[10px] text-muted-foreground'
              : 'mt-1 text-[10px] text-primary-foreground/70'
          }
        >
          {fmtRelative(message.createdAt)}
          {message.repliedAt && fromCustomer && ' · replied'}
        </p>
      </div>
    </div>
  );
}

function InboxSettingsPanel({
  settings,
  onSave,
}: {
  settings: CommInboxSettings;
  onSave: (patch: Partial<CommInboxSettings>) => Promise<void>;
}) {
  const [threshold, setThreshold] = useState(settings.awaitingReplyThresholdHours);
  const [auto, setAuto] = useState(settings.commInboxAutoSuggest);
  const [saving, setSaving] = useState(false);
  return (
    <div className="border-b border-border bg-card px-3 py-2.5">
      <p className="mb-1.5 text-xs font-medium">Inbox settings</p>
      <label className="mb-2 block text-xs">
        Awaiting-reply threshold (hours)
        <input
          type="number"
          min={1}
          max={168}
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-xs"
        />
      </label>
      <label className="mb-2 flex items-center gap-1.5 text-xs">
        <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
        Auto-suggest AI reply on open
      </label>
      <button
        type="button"
        disabled={saving}
        onClick={async () => {
          setSaving(true);
          try {
            await onSave({
              awaitingReplyThresholdHours: threshold,
              commInboxAutoSuggest: auto,
            });
          } finally {
            setSaving(false);
          }
        }}
        className="w-full rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
